import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Edit,
  CheckCircle,
  Clock,
  User,
  Receipt,
  Search,
  X,
  Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyLine = { description: '', quantity: 1, unit_price: 0 };

function formatCurrency(v) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v || 0);
}

export default function PendingPayments() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [client, setClient] = useState('');
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const [saving, setSaving] = useState(false);
  const [collectId, setCollectId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pending-payments`);
      setTickets(res.data);
    } catch {
      toast.error('Error al cargar tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openNew = () => {
    setEditingTicket(null);
    setClient('');
    setLines([{ ...emptyLine }]);
    setDialogOpen(true);
  };

  const openEdit = (ticket) => {
    setEditingTicket(ticket);
    setClient(ticket.client);
    setLines(ticket.lines.map(l => ({ ...l })));
    setDialogOpen(true);
  };

  const addLine = () => setLines(prev => [...prev, { ...emptyLine }]);

  const removeLine = (idx) => {
    if (lines.length <= 1) return;
    setLines(prev => prev.filter((_, i) => i !== idx));
  };

  const updateLine = (idx, field, value) => {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const lineTotal = (l) => Math.max(0, (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0));
  const grandTotal = lines.reduce((sum, l) => sum + lineTotal(l), 0);

  const handleSave = async () => {
    if (!client.trim()) { toast.error('Introduce el nombre del cliente'); return; }
    const hasEmpty = lines.some(l => !l.description.trim());
    if (hasEmpty) { toast.error('Todas las líneas necesitan descripción'); return; }
    const hasNeg = lines.some(l => parseFloat(l.quantity) <= 0 || parseFloat(l.unit_price) < 0);
    if (hasNeg) { toast.error('Sin valores negativos ni cantidades cero'); return; }

    setSaving(true);
    const payload = {
      client: client.trim(),
      lines: lines.map(l => ({
        description: l.description.trim(),
        quantity: parseFloat(l.quantity) || 1,
        unit_price: parseFloat(l.unit_price) || 0
      }))
    };
    try {
      if (editingTicket) {
        await axios.put(`${API}/pending-payments/${editingTicket.id}`, payload);
        toast.success('Ticket actualizado');
      } else {
        await axios.post(`${API}/pending-payments`, payload);
        toast.success('Ticket creado');
      }
      setDialogOpen(false);
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleCollect = async () => {
    if (!collectId) return;
    try {
      await axios.put(`${API}/pending-payments/${collectId}/collect`);
      toast.success('Cobrado');
      setCollectId(null);
      fetchTickets();
    } catch {
      toast.error('Error al cobrar');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/pending-payments/${deleteId}`);
      toast.success('Ticket eliminado');
      setDeleteId(null);
      fetchTickets();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const filtered = tickets.filter(t =>
    t.client.toLowerCase().includes(search.toLowerCase())
  );

  const totalPending = filtered.reduce((s, t) => s + (t.total || 0), 0);

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="pending-payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="page-title">
            Pendientes de Cobro
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {filtered.length} {filtered.length === 1 ? 'ticket' : 'tickets'} &middot; Total: <span className="font-semibold text-slate-700">{formatCurrency(totalPending)}</span>
          </p>
        </div>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-ticket-btn">
          <Plus size={16} className="mr-1" /> Nuevo Ticket
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Buscar por cliente..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
          data-testid="search-input"
        />
      </div>

      {/* Tickets list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner w-8 h-8" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Receipt size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="text-slate-400">{search ? 'Sin resultados' : 'No hay tickets pendientes'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map(ticket => (
            <Card key={ticket.id} className="hover:shadow-md transition-shadow" data-testid={`ticket-${ticket.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Client & date */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-slate-400 shrink-0" />
                      <span className="font-semibold text-slate-800 truncate" data-testid="ticket-client">{ticket.client}</span>
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs shrink-0" data-testid="ticket-days">
                        <Clock size={10} className="mr-1" />
                        {ticket.days_pending} {ticket.days_pending === 1 ? 'día' : 'días'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                      <span>{new Date(ticket.created_at).toLocaleDateString('es-ES')}</span>
                      <span>{ticket.lines.length} {ticket.lines.length === 1 ? 'línea' : 'líneas'}</span>
                      {ticket.created_by_name && <span>por {ticket.created_by_name}</span>}
                    </div>
                  </div>

                  {/* Total */}
                  <div className="text-right shrink-0">
                    <p className="text-xl font-bold text-slate-800 font-['Barlow_Condensed']" data-testid="ticket-total">
                      {formatCurrency(ticket.total)}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(ticket)} data-testid="edit-ticket-btn">
                      <Edit size={14} />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50" onClick={() => setDeleteId(ticket.id)} data-testid="delete-ticket-btn">
                      <Trash2 size={14} />
                    </Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setCollectId(ticket.id)} data-testid="collect-ticket-btn">
                      <CheckCircle size={14} className="mr-1" /> Cobrar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ticket-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">
              {editingTicket ? 'Editar Ticket' : 'Nuevo Ticket'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Client */}
            <div className="space-y-1">
              <Label>Cliente</Label>
              <Input
                value={client}
                onChange={e => setClient(e.target.value)}
                placeholder="Nombre del cliente"
                data-testid="client-input"
              />
            </div>

            {/* Lines */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Líneas</Label>
                <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn">
                  <Plus size={14} className="mr-1" /> Línea
                </Button>
              </div>
              <div className="space-y-2">
                {lines.map((line, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-lg p-3 space-y-2" data-testid={`line-${idx}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <Input
                          placeholder="Descripción"
                          value={line.description}
                          onChange={e => updateLine(idx, 'description', e.target.value)}
                          data-testid={`line-desc-${idx}`}
                        />
                      </div>
                      {lines.length > 1 && (
                        <Button size="icon" variant="ghost" className="text-red-400 hover:text-red-600 shrink-0 h-9 w-9" onClick={() => removeLine(idx)} data-testid={`remove-line-${idx}`}>
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs text-slate-400">Cantidad</Label>
                        <Input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={line.quantity}
                          onChange={e => updateLine(idx, 'quantity', e.target.value)}
                          data-testid={`line-qty-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400">P. Unitario</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.unit_price}
                          onChange={e => updateLine(idx, 'unit_price', e.target.value)}
                          data-testid={`line-price-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400">Total</Label>
                        <div className="h-9 flex items-center px-3 bg-white rounded-md border text-sm font-medium text-slate-700" data-testid={`line-total-${idx}`}>
                          {formatCurrency(lineTotal(line))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Grand total */}
            <div className="flex items-center justify-between bg-emerald-50 rounded-lg p-4">
              <span className="font-medium text-emerald-800">Total Ticket</span>
              <span className="text-2xl font-bold text-emerald-800 font-['Barlow_Condensed']" data-testid="dialog-total">
                {formatCurrency(grandTotal)}
              </span>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-ticket-btn">
              {saving ? 'Guardando...' : (<><Save size={14} className="mr-1" /> Guardar</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collect confirmation */}
      <AlertDialog open={!!collectId} onOpenChange={() => setCollectId(null)}>
        <AlertDialogContent data-testid="collect-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar como cobrado</AlertDialogTitle>
            <AlertDialogDescription>
              El ticket se eliminará de la lista de pendientes. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleCollect} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-collect-btn">
              Cobrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar ticket</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará este ticket permanentemente. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
